import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  LikertScaleBlock,
  LikertScaleOptionCount,
  LikertScaleType,
  ThemeTokens,
} from '@course-authoring/shared-types';
import {
  getLikertScaleOptionDescriptors,
  isRichTextContentEmpty,
  useAccountConfig,
  useResultsHidden,
  useTheme,
} from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  safeGenerateNonEmptyRichTextHTML,
} from './richTextRenderer';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
import { useSubmitRevealTransition } from './hooks/useSubmitRevealTransition';
import { useCommittedQuestionNarrationOutcome } from './hooks/useCommittedQuestionNarrationOutcome';
import { createSubmitRevealStageStyles } from './hooks/submitRevealStageStyles';
import { SubmitRevealButton } from './SubmitRevealButton';
import { createContentBlockStyles } from './styles/contentBlocks';
import { useCardBackground } from './SectionSurfaceContext';
import { Dropdown, type DropdownOption } from './Dropdown';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { getStandardInteractionWidth } from './interactionLayout';
import { ThankYouMessage } from './ThankYouMessage';
import type { QuestionNarrationOutcomeHandler } from './narrationEvents';
import { ResponseThresholdEmptyState } from './ResponseThresholdEmptyState';
import { localizeResultsDistribution } from './resultsLocalization';
import { RichTextContent } from './RichTextContent';

const DEFAULT_SCALE: LikertScaleType = 'agreement';
const DEFAULT_OPTION_COUNT: LikertScaleOptionCount = 7;
// Original desktop column pitch: 49px dot + 57px gap. Caps the fluid scale so
// wide screens keep today's spacing, then columns shrink to fit below it.
const LIKERT_OPTION_COLUMN_PX = 106;
// Below this the fluid horizontal 7-point scale gets too cramped, so the
// question input switches to the stacked mobile layout (AA-173). Likert-specific
// — above the generic 576/768 breakpoints because that's where the scale breaks.
const LIKERT_MOBILE_LAYOUT_BREAKPOINT = 850;
// The results views (fluid bars, tab bar) survive narrower than the question
// scale, so they switch to their mobile layout at a separate, lower breakpoint.
const LIKERT_RESULTS_MOBILE_BREAKPOINT = 768;
const LIKERT_SUBMIT_TRANSITION_MS = 180;
const LIKERT_RESULTS_ENTER_DELAY_MS = 20;
const LIKERT_RESULTS_ENTER_MS = 220;
const LIKERT_RESULTS_CHART_FILL_MS = 420;
const LIKERT_RESULTS_CHART_FILL_DELAY_MS = 80;
const contentResetStyles = createRichTextContentResetStyles('likert-scale-description');

type LikertResultGroup = 'left' | 'neutral' | 'right';
type LikertResultsTab = 'summary' | 'detail' | 'table';

export type LikertResultsDistribution = {
  id: 'my-org' | 'global';
  responseCount: number | string;
  leftPercentage: number;
  neutralPercentage: number;
  rightPercentage: number;
  optionPercentages: number[];
};

type LocalizedLikertResultsDistribution = LikertResultsDistribution & {
  title: string;
  responsesLabel: string;
};

type LikertChartSegment = {
  id: LikertResultGroup;
  label: string;
  start: number;
  width: number;
  fill: string;
};

type LikertDetailSegment = {
  label: string;
  start: number;
  width: number;
  fill: string;
  isFirst: boolean;
  isLast: boolean;
};

// Colors come straight from tokens.likert.* — the pure helpers below take
// them as parameters so nothing here can drift from themeTokens.ts.
type LikertResultColors = Record<LikertResultGroup, string>;

const getLikertResultColors = (likert: ThemeTokens['likert']): LikertResultColors => ({
  left: likert.negative.strong,
  neutral: likert.neutral,
  right: likert.positive.strong,
});

const getLikertDetailColors = (
  likert: ThemeTokens['likert'],
  optionCount: LikertScaleOptionCount,
): readonly string[] =>
  optionCount === 5
    ? [
      likert.negative.strong,
      likert.negative.light,
      likert.neutral,
      likert.positive.medium,
      likert.positive.strong,
    ]
    : [
      likert.negative.strong,
      likert.negative.medium,
      likert.negative.light,
      likert.neutral,
      likert.positive.light,
      likert.positive.medium,
      likert.positive.strong,
    ];

// Placeholder data shown until real aggregate results are available; hosts
// can inject real data via the resultsDistributions prop.
const PLACEHOLDER_LIKERT_RESULTS_DISTRIBUTIONS: readonly LikertResultsDistribution[] = [
  {
    id: 'my-org',
    responseCount: 55_100,
    leftPercentage: 61,
    neutralPercentage: 15,
    rightPercentage: 24,
    optionPercentages: [14, 10, 6, 15, 8, 9, 38],
  },
  {
    id: 'global',
    responseCount: 275_400,
    leftPercentage: 39,
    neutralPercentage: 36,
    rightPercentage: 25,
    optionPercentages: [14, 10, 6, 15, 8, 10, 37],
  },
] as const;

const PLACEHOLDER_LIKERT_RESULTS_WITHOUT_ORG = PLACEHOLDER_LIKERT_RESULTS_DISTRIBUTIONS.filter(
  (d) => d.id !== 'my-org',
);

function getLikertResultGroup(
  value: number,
  optionCount: LikertScaleOptionCount,
): LikertResultGroup {
  const neutralValue = Math.ceil(optionCount / 2);

  if (value === neutralValue) {
    return 'neutral';
  }

  return value < neutralValue ? 'left' : 'right';
}

function buildLikertChartSegments(
  distribution: LikertResultsDistribution,
  resultColors: LikertResultColors,
): LikertChartSegment[] {
  const left = distribution.leftPercentage;
  const neutral = distribution.neutralPercentage;
  const right = distribution.rightPercentage;

  return [
    {
      id: 'left',
      label: `${left}%`,
      start: 0,
      width: left,
      fill: resultColors.left,
    },
    {
      id: 'neutral',
      label: `${neutral}%`,
      start: left,
      width: neutral,
      fill: resultColors.neutral,
    },
    {
      id: 'right',
      label: `${right}%`,
      start: left + neutral,
      width: right,
      fill: resultColors.right,
    },
  ];
}

function buildLikertDetailSegments(
  optionPercentages: readonly number[],
  optionCount: LikertScaleOptionCount,
  colors: readonly string[],
  fallbackFill: string,
): LikertDetailSegment[] {
  let start = 0;

  return optionPercentages.slice(0, optionCount).map((pct, i) => {
    const segment: LikertDetailSegment = {
      label: `${pct}%`,
      start,
      width: pct,
      fill: colors[i] ?? fallbackFill,
      isFirst: i === 0,
      isLast: i === optionCount - 1,
    };
    start += pct;
    return segment;
  });
}

function getDistributionOptionPercentages(
  distribution: LikertResultsDistribution,
  optionCount: LikertScaleOptionCount,
): number[] {
  const { optionPercentages } = distribution;
  // Real data always has exactly one percentage per real option - pass through
  // directly. The placeholder is authored as a fixed 7-element array reused for
  // both 5- and 7-point scales, so a 5-point scale reading it needs the curated
  // 5-of-7 slots that align with getLikertDetailColors' 5-color mapping, not a
  // naive first-5 slice.
  if (optionPercentages.length === optionCount) {
    return [...optionPercentages];
  }
  if (optionCount === 5 && optionPercentages.length === 7) {
    return [
      optionPercentages[0] ?? 0,
      optionPercentages[2] ?? 0,
      optionPercentages[3] ?? 0,
      optionPercentages[5] ?? 0,
      optionPercentages[6] ?? 0,
    ];
  }
  return optionPercentages.slice(0, optionCount);
}

// Results labels shrink on small mobile so bar-segment percentages and the
// scale-end axis labels fit the narrow width (AA-173).
function getResultsSegmentLabelStyle(
  styles: ReturnType<typeof createContentBlockStyles>,
  tokens: ThemeTokens,
  isMobileLayout: boolean,
): React.CSSProperties {
  return isMobileLayout
    ? { ...styles.likertResultsChartSegmentLabel, fontSize: tokens.typography.sizes.xs }
    : styles.likertResultsChartSegmentLabel;
}

function getResultsAxisLabelStyle(
  styles: ReturnType<typeof createContentBlockStyles>,
  tokens: ThemeTokens,
  isMobileLayout: boolean,
): React.CSSProperties {
  return isMobileLayout
    ? { ...styles.likertResultsAxisLabel, fontSize: tokens.typography.sizes.sm }
    : styles.likertResultsAxisLabel;
}

/* ------------------------------------------------------------------ */
/*  Detail Tab                                                        */
/* ------------------------------------------------------------------ */

interface LikertDetailTabProps {
  distributions: readonly LocalizedLikertResultsDistribution[];
  optionCount: LikertScaleOptionCount;
  optionLabels: readonly string[];
  styles: ReturnType<typeof createContentBlockStyles>;
  prefersReducedMotion: boolean;
  isMobileLayout: boolean;
}

function LikertDetailTab({
  distributions,
  optionCount,
  optionLabels,
  styles,
  prefersReducedMotion,
  isMobileLayout,
}: Omit<LikertDetailTabProps, 'detailFillTriggeredRef'>): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const segmentLabelStyle = getResultsSegmentLabelStyle(styles, tokens, isMobileLayout);
  const axisLabelStyle = getResultsAxisLabelStyle(styles, tokens, isMobileLayout);
  const detailColors = useMemo(
    () => getLikertDetailColors(tokens.likert, optionCount),
    [tokens.likert, optionCount],
  );
  const [fillRevealed, setFillRevealed] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setFillRevealed(true));
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const leftAxisLabel = optionLabels[0] ?? '';
  const middleAxisLabel = optionLabels[Math.floor(optionLabels.length / 2)] ?? '';
  const rightAxisLabel = optionLabels[optionLabels.length - 1] ?? '';

  const detailFillStyle: React.CSSProperties = prefersReducedMotion
    ? { position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }
    : {
      position: 'relative',
      width: fillRevealed ? '100%' : '0%',
      height: '100%',
      overflow: 'hidden',
      transition: `width ${LIKERT_RESULTS_CHART_FILL_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${LIKERT_RESULTS_CHART_FILL_DELAY_MS}ms`,
    };

  // Small mobile: stacked per-option rows instead of a single segmented bar
  // (the segmented bar's smallest slices can't fit their % labels on a phone).
  if (isMobileLayout) {
    return (
      <div style={styles.likertResultsTabContent} data-testid="likert-results-detail">
        {distributions.map((distribution, index) => {
          const pcts = getDistributionOptionPercentages(distribution, optionCount);

          return (
            <div
              key={distribution.id}
              style={{
                ...styles.likertResultsDistribution,
                ...(index > 0 ? { marginTop: tokens.spacing.md } : {}),
              }}
              data-testid={`likert-detail-${distribution.id}`}
            >
              <div style={styles.likertResultsDistributionHeader}>
                <span style={styles.likertResultsDistributionTitle}>
                  {distribution.title}
                </span>
                <span style={styles.likertResultsDistributionResponses}>
                  {distribution.responsesLabel}
                </span>
              </div>

              <div style={styles.likertDetailRowsList}>
                {pcts.map((pct, i) => (
                  <div key={`${distribution.id}-row-${i}`} style={styles.likertDetailRow}>
                    <div style={styles.likertDetailRowHeader}>
                      <span style={styles.likertDetailRowLabel}>{optionLabels[i] ?? ''}</span>
                      <span style={styles.likertDetailRowPercent}>{pct}%</span>
                    </div>
                    <div
                      style={styles.likertDetailTrack}
                      role="img"
                      aria-label={`${optionLabels[i] ?? ''}: ${pct}%`}
                    >
                      <div
                        style={{
                          ...styles.likertDetailFill,
                          backgroundColor: detailColors[i] ?? tokens.likert.neutral,
                          width: prefersReducedMotion || fillRevealed ? `${pct}%` : '0%',
                          ...(prefersReducedMotion
                            ? {}
                            : {
                              transition: `width ${LIKERT_RESULTS_CHART_FILL_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${LIKERT_RESULTS_CHART_FILL_DELAY_MS}ms`,
                            }),
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={styles.likertResultsTabContent} data-testid="likert-results-detail">
      {distributions.map((distribution) => {
        const pcts = getDistributionOptionPercentages(distribution, optionCount);
        const segments = buildLikertDetailSegments(pcts, optionCount, detailColors, tokens.likert.neutral);

        return (
          <div
            key={distribution.id}
            style={styles.likertResultsDistribution}
            data-testid={`likert-detail-${distribution.id}`}
          >
            <div style={styles.likertResultsDistributionHeader}>
              <span style={styles.likertResultsDistributionTitle}>
                {distribution.title}
              </span>
              <span style={styles.likertResultsDistributionResponses}>
                {distribution.responsesLabel}
              </span>
            </div>

            <div style={styles.likertResultsChartSurface}>
              <div
                style={styles.likertResultsChartTrack}
                role="img"
                aria-label={t('results.detailed_results', { title: distribution.title })}
              >
                <div style={detailFillStyle}>
                  {segments.map((segment, i) => (
                    <div
                      key={`${distribution.id}-detail-${i}`}
                      style={{
                        ...styles.likertResultsChartSegment,
                        left: `${segment.start}%`,
                        width: `${segment.width}%`,
                        backgroundColor: segment.fill,
                        borderRadius: segment.isFirst
                          ? '5px 0 0 5px'
                          : segment.isLast
                            ? '0 5px 5px 0'
                            : '0',
                      }}
                      title={`${optionLabels[i] ?? ''}: ${segment.label}`}
                    >
                      <span style={segmentLabelStyle}>
                        {segment.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div style={styles.likertResultsAxisRow}>
        <span style={{ ...axisLabelStyle, textAlign: 'left' }}>
          {leftAxisLabel}
        </span>
        <span style={{ ...axisLabelStyle, textAlign: 'center' }}>
          {middleAxisLabel}
        </span>
        <span style={{ ...axisLabelStyle, textAlign: 'right' }}>
          {rightAxisLabel}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Table Tab                                                         */
/* ------------------------------------------------------------------ */

interface LikertTableTabProps {
  distributions: readonly LocalizedLikertResultsDistribution[];
  optionCount: LikertScaleOptionCount;
  optionLabels: readonly string[];
  styles: ReturnType<typeof createContentBlockStyles>;
  isMobileLayout: boolean;
}

const LIKERT_TABLE_HEADER_FONT_MAX = 16;
const LIKERT_TABLE_HEADER_FONT_MIN = 11;
const LIKERT_TABLE_LABEL_COL_FRACTION = 0.18;

function getHeaderFontSize(tableWidth: number, optionCount: number): number {
  const colWidth = (tableWidth * (1 - LIKERT_TABLE_LABEL_COL_FRACTION)) / optionCount;
  const scaled = Math.floor(colWidth / 7.5);
  return Math.max(LIKERT_TABLE_HEADER_FONT_MIN, Math.min(LIKERT_TABLE_HEADER_FONT_MAX, scaled));
}

function LikertTableTab({
  distributions,
  optionCount,
  optionLabels,
  styles,
  isMobileLayout,
}: LikertTableTabProps): React.ReactElement {
  const tableRef = useRef<HTMLTableElement>(null);
  const [headerFontSize, setHeaderFontSize] = useState(LIKERT_TABLE_HEADER_FONT_MAX);

  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;

    const update = () => {
      setHeaderFontSize(getHeaderFontSize(el.offsetWidth, optionCount));
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [optionCount]);

  // Small mobile: transpose per the Figma spec — scale options become rows,
  // distributions become the (narrow-friendly) columns.
  if (isMobileLayout) {
    const pctsByDistribution = distributions.map((distribution) =>
      getDistributionOptionPercentages(distribution, optionCount),
    );

    return (
      <div style={styles.likertResultsTabContent} data-testid="likert-results-table">
        <table style={styles.likertTableMobile}>
          <thead>
            <tr>
              <th style={{ ...styles.likertTableMobileCornerCell, width: '34%' }} />
              {distributions.map((distribution) => (
                <th
                  key={distribution.id}
                  style={{ ...styles.likertTableMobileHeaderCell, width: '33%' }}
                >
                  <span style={{ fontWeight: 700 }}>{distribution.title}</span>
                  <br />
                  <span>{distribution.responsesLabel}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {optionLabels.slice(0, optionCount).map((label, i) => (
              <tr key={label}>
                <td style={styles.likertTableMobileRowLabel}>{label}</td>
                {distributions.map((distribution, distIndex) => (
                  <td
                    key={`${distribution.id}-${i}`}
                    style={styles.likertTableMobileCell}
                  >
                    {pctsByDistribution[distIndex]?.[i] ?? 0}%
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div style={styles.likertResultsTabContent} data-testid="likert-results-table">
      <table ref={tableRef} style={styles.likertResultsTable}>
        <thead>
          <tr style={styles.likertResultsTableHeader}>
            <th
              style={{
                ...styles.likertResultsTableHeaderCell,
                borderLeft: 'none',
                width: '18%',
              }}
            />
            {optionLabels.slice(0, optionCount).map((label) => (
              <th
                key={label}
                style={{
                  ...styles.likertResultsTableHeaderCell,
                  fontSize: `${headerFontSize}px`,
                  wordBreak: 'break-word' as const,
                  overflowWrap: 'anywhere' as const,
                  hyphens: 'auto' as const,
                }}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {distributions.map((distribution) => {
            const pcts = getDistributionOptionPercentages(distribution, optionCount);

            return (
              <tr key={distribution.id} style={styles.likertResultsTableRow}>
                <td style={styles.likertResultsTableRowLabel}>
                  <span style={{ fontWeight: 700 }}>{distribution.title}</span>
                  <br />
                  <span>{distribution.responsesLabel}</span>
                </td>
                {pcts.map((pct, i) => (
                  <td key={`${distribution.id}-${i}`} style={styles.likertResultsTableCell}>
                    {pct}%
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Small-mobile (≤576px) question input — AA-173                     */
/* ------------------------------------------------------------------ */

interface LikertMobileOptionsProps {
  optionLabels: readonly string[];
  scale: LikertScaleType;
  optionCount: LikertScaleOptionCount;
  selectedValue: number | null;
  interactionComplete: boolean;
  onSelect: (value: number) => void;
  styles: ReturnType<typeof createContentBlockStyles>;
}

/**
 * Full-width stacked radio rows. Replaces the horizontal numbered-dot grid at
 * the small-mobile breakpoint, where the dot row overflows the viewport.
 */
function LikertMobileOptions({
  optionLabels,
  scale,
  optionCount,
  selectedValue,
  interactionComplete,
  onSelect,
  styles,
}: LikertMobileOptionsProps): React.ReactElement {
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);

  return (
    <div
      style={styles.likertMobileCard}
      data-testid="likert-mobile-options"
    >
      <div style={styles.likertMobileOptionsList}>
        {optionLabels.map((label, index) => {
          const value = index + 1;
          const isSelected = selectedValue === value;
          const isHovered =
            hoveredValue === value && !isSelected && !interactionComplete;

          return (
            <button
              key={`mobile-${scale}-${optionCount}-${value}`}
              type="button"
              aria-label={`${value}. ${label}`}
              aria-pressed={isSelected}
              disabled={interactionComplete}
              onClick={() => onSelect(value)}
              onMouseEnter={() => setHoveredValue(value)}
              onMouseLeave={() => setHoveredValue(null)}
              onFocus={() => setHoveredValue(value)}
              onBlur={() => setHoveredValue(null)}
              style={{
                ...styles.likertMobileOptionRow,
                ...(isHovered ? styles.likertMobileOptionRowHover : {}),
                ...(isSelected ? styles.likertMobileOptionRowSelected : {}),
                ...(interactionComplete ? { cursor: 'default' } : {}),
              }}
            >
              <span
                style={{
                  ...styles.likertMobileRadio,
                  ...(isSelected ? styles.likertMobileRadioSelected : {}),
                }}
              />
              <span
                style={{
                  ...styles.likertMobileOptionLabel,
                  ...(isSelected ? styles.likertMobileOptionLabelSelected : {}),
                }}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Renderer                                                     */
/* ------------------------------------------------------------------ */

export interface LikertScaleRendererProps {
  block: LikertScaleBlock;
  className?: string;
  style?: React.CSSProperties;
  onInteractionComplete?: () => void;
  /**
   * Fires once on submit with the bank option id for the selected position
   * (`block.optionIds[selectedValue - 1]`); not fired when the block carries
   * no resolved option ids (NA-178 response logging).
   */
  onAnswerSubmit?: (answer: { optionId: string }) => void;
  onNarrationOutcomeCommitted?: QuestionNarrationOutcomeHandler;
  /**
   * Aggregate results to display; defaults to placeholder data. `'below-
   * threshold'` (NA-72) renders the "not enough responses" empty state
   * instead of a distributions array; `'pending'` renders a lightweight
   * loading message while the real results fetch is in flight.
   */
  resultsDistributions?: readonly LikertResultsDistribution[] | 'below-threshold' | 'pending';
  /**
   * Whether the player has found a prior recorded answer for this block on
   * bootstrap (NA-319). `'loading'` renders a lightweight placeholder in
   * place of both the question and results views, so a learner can't submit
   * a new answer while the player is still checking. Defaults to
   * `'unanswered'` (today's behavior) for any caller that doesn't opt in,
   * e.g. authoring previews.
   */
  priorAnswerStatus?: 'loading' | 'answered' | 'unanswered';
  /** The learner's previously recorded `answerOptionId`, present only when `priorAnswerStatus === 'answered'`. */
  priorAnswerOptionId?: string;
}

/** 1-based Likert position for a bank answerOptionId, or null if not found/absent. */
function getLikertPositionFromOptionId(
  optionIds: readonly string[] | undefined,
  answerOptionId: string | undefined
): number | null {
  if (!optionIds || !answerOptionId) return null;
  const index = optionIds.indexOf(answerOptionId);
  return index === -1 ? null : index + 1;
}

export function LikertScaleRenderer({
  block,
  className,
  style,
  onInteractionComplete,
  onAnswerSubmit,
  onNarrationOutcomeCommitted,
  resultsDistributions: resultsDistributionsInput,
  priorAnswerStatus = 'unanswered',
  priorAnswerOptionId,
}: LikertScaleRendererProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { hideOrg } = useAccountConfig();
  const resultsHidden = useResultsHidden(block.hideResults);
  // Placeholder data (Preview) must respect the account's My Org toggle (NA-344).
  const resultsDistributionsProp =
    resultsDistributionsInput ??
    (hideOrg ? PLACEHOLDER_LIKERT_RESULTS_WITHOUT_ORG : PLACEHOLDER_LIKERT_RESULTS_DISTRIBUTIONS);
  const isBelowResponseThreshold = resultsDistributionsProp === 'below-threshold';
  const isResultsPending = resultsDistributionsProp === 'pending';
  const resultsDistributions = useMemo<readonly LocalizedLikertResultsDistribution[]>(() => {
    if (typeof resultsDistributionsProp === 'string') return [];
    const locale = i18n.resolvedLanguage ?? i18n.language;
    return resultsDistributionsProp.map((distribution) => {
      return {
        ...distribution,
        ...localizeResultsDistribution(t, locale, distribution, {
          myOrgTitle: 'results.my_organization',
          globalTitle: 'results.global_community',
          responsesOne: 'results.responses_one',
          responsesOther: 'results.responses_other',
        }),
      };
    });
  }, [i18n.language, i18n.resolvedLanguage, resultsDistributionsProp, t]);
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  // Two independent layout switches (AA-173): the question input's dot scale
  // gets cramped earlier (850) than the results views (768), so they flip at
  // different widths. Both separate from the 768px interaction-width tweak above.
  const isQuestionMobileLayout = useIsMobileViewport(LIKERT_MOBILE_LAYOUT_BREAKPOINT);
  const isResultsMobileLayout = useIsMobileViewport(LIKERT_RESULTS_MOBILE_BREAKPOINT);
  const prefersReducedMotion = usePrefersReducedMotion();
  // Desktop card stays surface-aware; the mobile card uses a fixed cardAlt to
  // match Single Choice (see likertMobileCard).
  const cardBackground = useCardBackground();
  const styles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const resultColors = useMemo(() => getLikertResultColors(tokens.likert), [tokens.likert]);
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);
  const initialPriorPosition =
    priorAnswerStatus === 'answered'
      ? getLikertPositionFromOptionId(block.optionIds, priorAnswerOptionId)
      : null;
  const [selectedValue, setSelectedValue] = useState<number | null>(() => initialPriorPosition);
  const [interactionComplete, setInteractionComplete] = useState(() => initialPriorPosition !== null);
  const [activeResultsTab, setActiveResultsTab] = useState<LikertResultsTab>('summary');
  const resultsTabOptions = useMemo<readonly DropdownOption<LikertResultsTab>[]>(() => [
    { value: 'summary', label: t('results.summary') },
    { value: 'detail', label: t('results.detail') },
    { value: 'table', label: t('results.table') },
  ], [t]);
  const interactionWidth = getStandardInteractionWidth(tokens, isMobile);
  const resultsSegmentLabelStyle = getResultsSegmentLabelStyle(styles, tokens, isResultsMobileLayout);
  const resultsAxisLabelStyle = getResultsAxisLabelStyle(styles, tokens, isResultsMobileLayout);
  const {
    transitionPhase,
    isQuestionStageVisible,
    isResultsStageVisible,
    isSubmitting,
    startTransition,
    resetTransition,
    revealImmediately,
  } = useSubmitRevealTransition({
    prefersReducedMotion,
    onInteractionComplete,
    submitTransitionMs: LIKERT_SUBMIT_TRANSITION_MS,
    resultsEnterDelayMs: LIKERT_RESULTS_ENTER_DELAY_MS,
    initialPhase: initialPriorPosition !== null ? 'results' : 'question',
  });

  // NA-319: the bootstrap prior-answers fetch normally resolves after this
  // block's first mount, not before it - so the lazy useState/initialPhase
  // seeding above only covers the (uncommon) case where it's already known
  // at mount. This effect covers the common case: the prop transitions from
  // 'loading' to 'answered' after mount, and must seed the same locked state
  // then, since a useState initializer never re-runs on a later prop update.
  useEffect(() => {
    if (priorAnswerStatus !== 'answered' || selectedValue !== null) return;
    const position = getLikertPositionFromOptionId(block.optionIds, priorAnswerOptionId);
    if (position === null) return;
    setSelectedValue(position);
    setInteractionComplete(true);
    revealImmediately();
  }, [priorAnswerStatus, priorAnswerOptionId, block.optionIds, selectedValue, revealImmediately]);

  const scale = block.scale ?? DEFAULT_SCALE;
  const optionCount = block.optionCount ?? DEFAULT_OPTION_COUNT;
  const optionLabels = useMemo(
    () => getLikertScaleOptionDescriptors(scale, optionCount)
      .map((option) => t(`likert_scale.options.${option.id}`)),
    [scale, optionCount, t],
  );

  useEffect(() => {
    if (selectedValue !== null && selectedValue > optionLabels.length) {
      setSelectedValue(null);
      setInteractionComplete(false);
      resetTransition();
    }
    if (hoveredValue !== null && hoveredValue > optionLabels.length) {
      setHoveredValue(null);
    }
    if (selectedValue === null && interactionComplete) {
      setInteractionComplete(false);
    }
    if (selectedValue === null && transitionPhase !== 'question') {
      resetTransition();
    }
  }, [hoveredValue, interactionComplete, optionLabels.length, resetTransition, selectedValue, transitionPhase]);

  const titleLevel = block.titleLevel ?? 'h2';
  const titleText = (block.title ?? '').trim();
  const hasTitle = titleText.length > 0;
  const hasDescription =
    block.description != null && !isRichTextContentEmpty(block.description);

  const descriptionHtml = useMemo(() => {
    if (!hasDescription || !block.description) {
      return null;
    }

    return safeGenerateNonEmptyRichTextHTML(block.description, extensions);
  }, [block.description, extensions, hasDescription]);

  // NA-227: same three-tier content-block heading resolution as WCS so the two
  // H2 titles size identically across viewports (40 → 30 <768 → 26 ≤576, with
  // the ≥26px → 1.25 line-height rule).
  const titleTypography = useMemo(
    () => getContentBlockHeadingTypography(tokens, titleLevel, { isMobile, isSmallMobile }),
    [isMobile, isSmallMobile, titleLevel, tokens],
  );

  const titleStyle: React.CSSProperties = useMemo(
    () => ({
      ...styles.likertTitle,
      fontSize: titleTypography.size,
      fontWeight: titleTypography.weight,
      lineHeight: titleTypography.lineHeight,
    }),
    [styles.likertTitle, titleTypography],
  );

  const handleSelect = useCallback(
    (value: number) => {
      if (interactionComplete) {
        return;
      }

      setSelectedValue(value);
    },
    [interactionComplete],
  );

  const handleSubmit = useCallback(() => {
    if (selectedValue === null || interactionComplete) {
      return;
    }

    // selectedValue is 1-based; optionIds is position-ordered to match the
    // regenerated labels. Blocks without resolved ids simply don't emit.
    const answerOptionId = block.optionIds?.[selectedValue - 1];
    if (answerOptionId) {
      onAnswerSubmit?.({ optionId: answerOptionId });
    }

    setHoveredValue(null);
    setInteractionComplete(true);
    startTransition();
  }, [interactionComplete, selectedValue, startTransition, block.optionIds, onAnswerSubmit]);

  const submitDisabled = selectedValue === null;
  const selectedAnswerLabel =
    selectedValue !== null ? (optionLabels[selectedValue - 1] ?? null) : null;
  const selectedAnswerGroup =
    selectedValue !== null ? getLikertResultGroup(selectedValue, optionCount) : null;
  const shouldShowResults = isResultsStageVisible && selectedAnswerLabel !== null && selectedAnswerGroup !== null;
  useCommittedQuestionNarrationOutcome({
    outcomeKey: shouldShowResults && selectedValue !== null
      ? `${block.id}:${selectedValue}:${resultsHidden ? 'hidden' : 'visible'}`
      : null,
    outcome: shouldShowResults ? 'submitted' : null,
    presentation: shouldShowResults
      ? (resultsHidden ? 'hidden-results' : 'visible-results')
      : null,
    onCommitted: onNarrationOutcomeCommitted,
  });
  const middleOptionIndex = Math.floor(optionLabels.length / 2);
  const middleOptionValue = middleOptionIndex + 1;
  const isAnchorOption = (v: number) =>
    v === 1 || v === middleOptionValue || v === optionLabels.length;

  const activeTooltipValue =
    transitionPhase === 'question' ? hoveredValue : null;
  const leftAxisLabel = optionLabels[0] ?? '';
  const middleAxisLabel = optionLabels[middleOptionIndex] ?? '';
  const rightAxisLabel = optionLabels[optionLabels.length - 1] ?? '';
  const resultGroupLabels: Record<LikertResultGroup, string> = {
    left: leftAxisLabel,
    neutral: t('results.neutral'),
    right: rightAxisLabel,
  };
  const submitAccentColor =
    selectedAnswerGroup !== null
      ? resultColors[selectedAnswerGroup]
      : tokens.backgrounds.inverse;
  const resultsBadgeColor =
    selectedAnswerGroup !== null
      ? resultColors[selectedAnswerGroup]
      : tokens.backgrounds.inverse;
  const { questionStage: questionStageStyle, resultsStage: resultsStageStyle } =
    createSubmitRevealStageStyles({
      prefersReducedMotion,
      transitionPhase,
      submitTransitionMs: LIKERT_SUBMIT_TRANSITION_MS,
      resultsEnterMs: LIKERT_RESULTS_ENTER_MS,
    });
  const chartFillRevealStyle: React.CSSProperties = prefersReducedMotion
    ? {
      position: 'relative',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    }
    : {
      position: 'relative',
      width: transitionPhase === 'results' ? '100%' : '0%',
      height: '100%',
      overflow: 'hidden',
      transition: `width ${LIKERT_RESULTS_CHART_FILL_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${LIKERT_RESULTS_CHART_FILL_DELAY_MS}ms`,
    };

  return (
    <div
      className={className}
      style={{
        ...styles.likertContainer,
        width: interactionWidth,
        maxWidth: '100%',
        margin: '0 auto',
        ...style,
      }}
      data-testid="likert-scale-container"
    >
      <style>{contentResetStyles}</style>

      {hasTitle || descriptionHtml ? (
        <div style={styles.likertHeader}>
          {hasTitle
            ? React.createElement(titleLevel, { style: titleStyle }, titleText)
            : null}
          {descriptionHtml ? (
            <RichTextContent
              className="likert-scale-description"
              // NA-205/NA-227: match WCS — reading copy steps to 16px at ≤576.
              style={
                isSmallMobile
                  ? { ...styles.likertDescription, fontSize: tokens.typography.mobile.body.size }
                  : styles.likertDescription
              }
              html={descriptionHtml}
            />
          ) : null}
        </div>
      ) : null}

      {priorAnswerStatus === 'loading' ? (
        <ResponseThresholdEmptyState variant="checking-answer" />
      ) : (
        <>
          {isQuestionStageVisible ? (
            <div style={questionStageStyle} data-testid="likert-question-stage">
              {isQuestionMobileLayout ? (
                <LikertMobileOptions
                  optionLabels={optionLabels}
                  scale={scale}
                  optionCount={optionCount}
                  selectedValue={selectedValue}
                  interactionComplete={interactionComplete}
                  onSelect={handleSelect}
                  styles={styles}
                />
              ) : (
                <div style={{ ...styles.likertCard, backgroundColor: cardBackground }}>
                  <div style={styles.likertOptionsScroller}>
                    <div style={styles.likertOptionsLayout}>
                      <div
                        style={{
                          ...styles.likertOptionsGrid,
                          gridTemplateColumns: `repeat(${optionLabels.length}, minmax(0, 1fr))`,
                          maxWidth: `${optionLabels.length * LIKERT_OPTION_COLUMN_PX}px`,
                        }}
                      >
                        {optionLabels.map((label, index) => {
                          const value = index + 1;
                          const isSelected = selectedValue === value;
                          const isHovered = hoveredValue === value && !interactionComplete;

                          return (
                            <button
                              key={`${scale}-${optionCount}-${value}`}
                              type="button"
                              aria-label={`${value}. ${label}`}
                              aria-pressed={isSelected}
                              disabled={interactionComplete}
                              onClick={() => handleSelect(value)}
                              onPointerUp={(event) => {
                                event.currentTarget.blur();
                              }}
                              onMouseEnter={() => setHoveredValue(value)}
                              onMouseLeave={() => setHoveredValue(null)}
                              onFocus={() => setHoveredValue(value)}
                              onBlur={() => setHoveredValue(null)}
                              style={{
                                ...styles.likertOptionButton,
                                ...(isHovered ? styles.likertOptionButtonHover : {}),
                                ...(isSelected ? styles.likertOptionButtonSelected : {}),
                                ...(interactionComplete ? { cursor: 'default' } : {}),
                              }}
                            >
                              <span
                                style={{
                                  ...styles.likertOptionNumber,
                                  ...(isHovered
                                    ? {
                                      backgroundColor: tokens.backgrounds.surfaceAlt,
                                    }
                                    : {}),
                                  ...(isSelected
                                    ? {
                                      ...styles.likertOptionNumberSelected,
                                      // NA-242: selection state uses the fixed UI accent —
                                      // never the account accent.
                                      backgroundColor: tokens.ui.fixedAccent,
                                      borderColor: tokens.ui.fixedAccent,
                                    }
                                    : {}),
                                }}
                              />
                            </button>
                          );
                        })}

                        {optionLabels.map((label, index) => {
                          const value = index + 1;
                          const isAnchor = isAnchorOption(value);
                          const isHoveredLabel = activeTooltipValue === value;
                          const isSelectedLabel = selectedValue === value;
                          const hasSelection = selectedValue !== null;
                          const showLabel = isHoveredLabel || isSelectedLabel || (isAnchor && !hasSelection);
                          const showCaret = isHoveredLabel;
                          const isDimmed = hasSelection && !isSelectedLabel && !isHoveredLabel;

                          return (
                            <div
                              key={`label-${scale}-${optionCount}-${value}`}
                              style={styles.likertOptionLabelCell}
                            >
                              {showCaret ? (
                                <span style={styles.likertOptionTooltipCaret} />
                              ) : null}
                              <span
                                style={{
                                  ...styles.likertAxisLabel,
                                  opacity: showLabel ? (isDimmed ? 0.5 : 1) : 0,
                                  transition: `opacity ${tokens.ui.transitionDefault}`,
                                }}
                              >
                                {label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <SubmitRevealButton
                disabled={submitDisabled}
                isSubmitting={isSubmitting}
                prefersReducedMotion={prefersReducedMotion}
                onSubmit={handleSubmit}
                baseStyle={styles.likertSubmitButton}
                disabledStyle={styles.likertSubmitButtonDisabled}
                hoverStyle={styles.actionButtonHover}
                accentColor={submitAccentColor}
                data-testid="likert-submit-button"
              />
            </div>
          ) : null}

          {shouldShowResults && resultsHidden ? (
            <div style={resultsStageStyle} data-testid="likert-results-stage">
              <ThankYouMessage visible />
            </div>
          ) : null}

          {shouldShowResults && !resultsHidden ? (
            <div style={resultsStageStyle} data-testid="likert-results-stage">
              <div style={styles.likertResultsAnswerRow}>
                <div style={styles.likertResultsAnswerLabel}>{t('results.your_answer')}</div>
                <div
                  style={{
                    ...styles.likertResultsAnswerBadge,
                    backgroundColor: resultsBadgeColor,
                  }}
                  data-testid="likert-results-answer-badge"
                >
                  {selectedAnswerLabel}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: tokens.backgrounds.resultsPanel,
                  border: styles.likertResultsCard.border,
                  borderRadius: '15px',
                  boxShadow: styles.likertResultsCard.boxShadow,
                  width: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                }}
                data-testid="likert-scale-results"
              >
                {isBelowResponseThreshold || isResultsPending ? (
                  <ResponseThresholdEmptyState variant={isResultsPending ? 'loading' : 'empty'} />
                ) : (
                  <>
                    {/* View selector: dropdown on small mobile, tab bar otherwise */}
                    {isResultsMobileLayout ? (
                      <div style={styles.likertResultsSelectWrap}>
                        <Dropdown
                          value={activeResultsTab}
                          options={resultsTabOptions}
                          onChange={setActiveResultsTab}
                          ariaLabel={t('results.view')}
                          data-testid="likert-results-select"
                        />
                      </div>
                    ) : (
                      <div style={styles.likertResultsTabBar}>
                        {(['summary', 'detail', 'table'] as const).map((tab, i) => {
                          const isActive = activeResultsTab === tab;
                          const tabLabel = t(`results.${tab}`);
                          return (
                            <button
                              key={tab}
                              type="button"
                              onClick={() => setActiveResultsTab(tab)}
                              style={{
                                ...styles.likertResultsTab,
                                ...(isActive ? styles.likertResultsTabActive : {}),
                                borderRadius:
                                  i === 0
                                    ? '13px 0 0 0'
                                    : i === 2
                                      ? '0 13px 0 0'
                                      : '0',
                              }}
                              data-testid={`likert-results-tab-${tab}`}
                            >
                              {tabLabel}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Summary Tab */}
                    {activeResultsTab === 'summary' ? (
                      <div style={styles.likertResultsTabContent}>
                        {resultsDistributions.map((distribution, index) => {
                          const chartSegments = buildLikertChartSegments(distribution, resultColors);

                          return (
                            <div
                              key={distribution.id}
                              style={{
                                ...styles.likertResultsDistribution,
                                ...(isResultsMobileLayout && index > 0 ? { marginTop: tokens.spacing.md } : {}),
                              }}
                              data-testid={`likert-chart-${distribution.id}`}
                            >
                              <div style={styles.likertResultsDistributionHeader}>
                                <span style={styles.likertResultsDistributionTitle}>
                                  {distribution.title}
                                </span>
                                <span style={styles.likertResultsDistributionResponses}>
                                  {distribution.responsesLabel}
                                </span>
                              </div>

                              <div style={styles.likertResultsChartSurface}>
                                <div
                                  style={styles.likertResultsChartTrack}
                                  role="img"
                                  aria-label={t('results.summary_aria', {
                                    title: distribution.title,
                                    left: distribution.leftPercentage,
                                    neutral: distribution.neutralPercentage,
                                    right: distribution.rightPercentage,
                                  })}
                                >
                                  <div
                                    style={chartFillRevealStyle}
                                    data-testid={`likert-chart-fill-${distribution.id}`}
                                  >
                                    {chartSegments.map((segment) => (
                                      <div
                                        key={`${distribution.id}-${segment.id}`}
                                        style={{
                                          ...styles.likertResultsChartSegment,
                                          left: `${segment.start}%`,
                                          width: `${segment.width}%`,
                                          backgroundColor: segment.fill,
                                          borderRadius:
                                            segment.id === 'left'
                                              ? '5px 0 0 5px'
                                              : segment.id === 'right'
                                                ? '0 5px 5px 0'
                                                : '0',
                                        }}
                                        title={`${resultGroupLabels[segment.id]}: ${segment.label}`}
                                      >
                                        <span style={resultsSegmentLabelStyle}>
                                          {segment.label}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        <div style={styles.likertResultsAxisRow}>
                          <span style={{ ...resultsAxisLabelStyle, textAlign: 'left' }}>
                            {leftAxisLabel}
                          </span>
                          <span style={{ ...resultsAxisLabelStyle, textAlign: 'center' }}>
                            {t('results.neutral')}
                          </span>
                          <span style={{ ...resultsAxisLabelStyle, textAlign: 'right' }}>
                            {rightAxisLabel}
                          </span>
                        </div>
                      </div>
                    ) : null}

                    {/* Detail Tab */}
                    {activeResultsTab === 'detail' ? (
                      <LikertDetailTab
                        distributions={resultsDistributions}
                        optionCount={optionCount}
                        optionLabels={optionLabels}
                        styles={styles}
                        prefersReducedMotion={prefersReducedMotion}
                        isMobileLayout={isResultsMobileLayout}
                      />
                    ) : null}

                    {/* Table Tab */}
                    {activeResultsTab === 'table' ? (
                      <LikertTableTab
                        distributions={resultsDistributions}
                        optionCount={optionCount}
                        optionLabels={optionLabels}
                        styles={styles}
                        isMobileLayout={isResultsMobileLayout}
                      />
                    ) : null}
                  </>
                )}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

LikertScaleRenderer.displayName = 'LikertScaleRenderer';
